import { normalizePrefIsActive } from "../emailTriaging/emailTriageUtils";
import { tntLinkageGetFilters } from "./tntLinkageApi";
import { DEFAULT_CONFIDENCE_FILTER_VALUES, TNT_PREF_NAME_LABEL_OVERRIDES, TNT_USER_PREFS_SCREEN_ID, normalizeTntFilterKey } from "./tntLinkageConstants";

export { normalizePrefIsActive } from "../emailTriaging/emailTriageUtils";
export {
  TNT_USER_PREFS_SCREEN_ID,
  buildEmptyTntFilterOptionsFromUserPrefs,
  buildDefaultTntSelectedFilters,
  normalizeTntFilterKey,
} from "./tntLinkageConstants";

export function humanizeTntPrefFilterName(prefName) {
  const raw = String(prefName ?? "").trim();
  if (!raw) return "";
  if (TNT_PREF_NAME_LABEL_OVERRIDES[raw]) return TNT_PREF_NAME_LABEL_OVERRIDES[raw];
  const normalized = normalizeTntFilterKey(raw);
  if (TNT_PREF_NAME_LABEL_OVERRIDES[normalized]) {
    return TNT_PREF_NAME_LABEL_OVERRIDES[normalized];
  }
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function generateTntFilterLabel(key, prefName) {
  if (prefName) return humanizeTntPrefFilterName(prefName);
  const normalized = normalizeTntFilterKey(key);
  if (TNT_PREF_NAME_LABEL_OVERRIDES[normalized]) {
    return TNT_PREF_NAME_LABEL_OVERRIDES[normalized];
  }
  return humanizeTntPrefFilterName(normalized);
}

function isConfidenceFilterKey(filterKey) {
  return normalizeTntFilterKey(filterKey) === "confidence";
}

function isLinkageStatusFilterKey(filterKey) {
  return normalizeTntFilterKey(filterKey) === "linkage_status";
}

function filterOptionList(filterOptions, ...keys) {
  const opts = filterOptions && typeof filterOptions === "object" ? filterOptions : {};
  for (const key of keys) {
    const normalized = normalizeTntFilterKey(key);
    const list = opts[normalized] ?? opts[key];
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
}

/** API confidence bucket → display label */
export const TNT_CONFIDENCE_VALUE_LABELS = {
  lt_60: "Less than 60",
  "60_to_80": "Between 60 to 80",
  gt_80: "Greater than 80",
};

/** API linkage_status → display label */
export const TNT_LINKAGE_STATUS_VALUE_LABELS = {
  matched: "Matched",
  rejected: "Rejected",
  action_not_taken: "Action Not Taken",
  user_validated: "User Validated",
};

export function formatTntFilterOptionLabel(filterKey, value) {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (isConfidenceFilterKey(filterKey)) {
    return TNT_CONFIDENCE_VALUE_LABELS[v] ?? v;
  }
  if (isLinkageStatusFilterKey(filterKey)) {
    return TNT_LINKAGE_STATUS_VALUE_LABELS[v] ?? v;
  }
  return v;
}

/** Options for UI filter controls: show friendly labels for mapped keys. */
export function formatTntFilterOptionsForDisplay(filterKey, options) {
  const list = Array.isArray(options) ? options : [];
  if (!isConfidenceFilterKey(filterKey) && !isLinkageStatusFilterKey(filterKey)) {
    return list;
  }
  return list.map((value) => formatTntFilterOptionLabel(filterKey, value));
}

/** Map `/user-prefs` filter `name` to internal filter key */
export function resolveTntPrefFilterNameToKey(name) {
  return normalizeTntFilterKey(name);
}

export function normalizePersistedTntSelectedFilters(filters) {
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) return {};
  const next = {};
  Object.entries(filters).forEach(([key, value]) => {
    const normalizedKey = normalizeTntFilterKey(key);
    if (!normalizedKey) return;
    next[normalizedKey] = Array.isArray(value) ? value : [];
  });
  return next;
}

export function mergeTntSelectedFiltersWithUserPrefs(
  selectedFilters,
  userPrefsFilters,
) {
  const next = normalizePersistedTntSelectedFilters(selectedFilters);
  if (Array.isArray(userPrefsFilters)) {
    for (const pf of userPrefsFilters) {
      const key = resolveTntPrefFilterNameToKey(pf.name);
      if (key && next[key] === undefined) next[key] = [];
    }
  }
  const hadConfidence =
    (Array.isArray(selectedFilters?.confidence) && selectedFilters.confidence.length > 0) ||
    (Array.isArray(selectedFilters?.confidenceScore) && selectedFilters.confidenceScore.length > 0);
  if ("confidence" in next && !hadConfidence && next.confidence.length === 0) {
    next.confidence = [...DEFAULT_CONFIDENCE_FILTER_VALUES];
  }
  return next;
}

export function buildRmpmFiltersFromUserPrefs(
  userPrefsFilters,
  { prev = [], selectedFilters = {} } = {},
) {
  if (!Array.isArray(userPrefsFilters) || userPrefsFilters.length === 0) return [];
  const sorted = [...userPrefsFilters].sort((a, b) => {
    const pa = Number(a?.priority);
    const pb = Number(b?.priority);
    const da = Number.isFinite(pa) ? pa : 999;
    const db = Number.isFinite(pb) ? pb : 999;
    return da - db;
  });
  const prevByKey = Object.fromEntries(
    prev.map((f) => [f.filterKey || resolveTntPrefFilterNameToKey(f.prefName), f]),
  );
  return sorted
    .map((pf, idx) => {
      const filterKey = resolveTntPrefFilterNameToKey(pf.name);
      if (!filterKey) return null;
      const label = generateTntFilterLabel(filterKey, pf.name);
      const po = Number(pf.priority);
      const previous = prevByKey[filterKey];
      return {
        filterKey,
        label,
        prefName: pf.name == null ? "" : String(pf.name),
        selectedData:
          previous?.selectedData ?? selectedFilters[filterKey] ?? [],
        isFavourite: normalizePrefIsActive(pf.is_active),
        DisplayOrder: Number.isFinite(po) ? po : idx + 1,
      };
    })
    .filter(Boolean);
}

/** Build POST body for `/api/v1/user-prefs` (TnT list screen). */
/** Best available SAP material description for a table row. */
export function materialDescFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const code = String(row.materialCode ?? "").trim();
  const agent = String(row.agentMaterialDesc ?? "").trim();
  if (agent && agent !== code) return agent;
  const tnt = String(row.tntMaterialDesc ?? "").trim();
  if (tnt && tnt !== code) return tnt;
  return "";
}

function isMeaningfulLabel(label, code) {
  const l = String(label ?? "").trim();
  const c = String(code ?? "").trim();
  return Boolean(l) && l !== "—" && l !== c;
}

/** Label from correction dropdown `{ code, name }` list. */
export function lookupCorrectionOptionLabel(code, entries) {
  const c = String(code ?? "").trim();
  if (!c) return "";
  const entry = (Array.isArray(entries) ? entries : []).find(
    (o) => String(o?.code ?? "").trim() === c,
  );
  const name = String(entry?.name ?? "").trim();
  return isMeaningfulLabel(name, c) ? name : "";
}

/** site_code → site_cluster from current rows + filter option lists. */
export function buildSiteCodeClusterMap(tableData, filterOptions) {
  const map = new Map();
  const rows = Array.isArray(tableData) ? tableData : [];
  const opts = filterOptions && typeof filterOptions === "object" ? filterOptions : {};

  const setPair = (code, cluster) => {
    const c = String(code ?? "").trim();
    const cl = String(cluster ?? "").trim();
    if (!c || !isMeaningfulLabel(cl, c)) return;
    if (!map.has(c)) map.set(c, cl);
  };

  rows.forEach((row) => setPair(row.siteCode, row.siteCluster));

  (filterOptionList(opts, "site_cluster", "siteCluster")).forEach((cluster) => {
    const cl = String(cluster).trim();
    if (!cl) return;
    const match = rows.find((r) => String(r.siteCluster).trim() === cl);
    if (match) setPair(match.siteCode, cl);
  });

  filterOptionList(opts, "unit_code", "siteCode").forEach((code) => {
    const k = String(code).trim();
    if (!k || map.has(k)) return;
    const match = rows.find((r) => String(r.siteCode).trim() === k);
    if (match) setPair(k, match.siteCluster);
  });

  return map;
}

/** material_code → description from current rows + filter option lists. */
export function buildMaterialCodeDescMap(tableData, filterOptions) {
  const map = new Map();
  const rows = Array.isArray(tableData) ? tableData : [];
  const opts = filterOptions && typeof filterOptions === "object" ? filterOptions : {};

  rows.forEach((row) => {
    const code = String(row.materialCode ?? "").trim();
    const desc = materialDescFromRow(row);
    if (code && isMeaningfulLabel(desc, code)) map.set(code, desc);
  });

  filterOptionList(opts, "item_description", "materialDesc", "materialdesc").forEach((desc) => {
    const d = String(desc).trim();
    if (!d) return;
    const match = rows.find(
      (r) =>
        String(r.agentMaterialDesc ?? "").trim() === d ||
        String(r.tntMaterialDesc ?? "").trim() === d,
    );
    if (match) {
      const code = String(match.materialCode ?? "").trim();
      if (code && isMeaningfulLabel(d, code)) map.set(code, d);
    }
  });

  filterOptionList(opts, "item_code", "materialId").forEach((code) => {
    const k = String(code).trim();
    if (!k || map.has(k)) return;
    const match = rows.find((r) => String(r.materialCode).trim() === k);
    if (match) {
      const desc = materialDescFromRow(match);
      if (isMeaningfulLabel(desc, k)) map.set(k, desc);
    }
  });

  return map;
}

export function resolveSiteClusterForCode(
  siteCode,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = String(siteCode ?? "").trim();
  if (!code) return "—";

  const fromOpt = (correctionOptions?.siteCode || []).find(
    (o) => String(o?.code ?? "").trim() === code,
  );
  const optName = String(fromOpt?.name ?? "").trim();
  if (isMeaningfulLabel(optName, code)) return optName;

  const rows = Array.isArray(tableData) ? tableData : [];
  for (const row of rows) {
    if (String(row.siteCode).trim() !== code) continue;
    const cluster = String(row.siteCluster ?? "").trim();
    if (isMeaningfulLabel(cluster, code)) return cluster;
  }

  const fromMap = buildSiteCodeClusterMap(tableData, filterOptions).get(code);
  if (fromMap && isMeaningfulLabel(fromMap, code)) return fromMap;

  return "—";
}

export function resolveMaterialDescForCode(
  materialCode,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = String(materialCode ?? "").trim();
  if (!code) return "—";

  const fromOpt = (correctionOptions?.material || []).find(
    (o) => String(o?.code ?? "").trim() === code,
  );
  const optName = String(fromOpt?.name ?? "").trim();
  if (isMeaningfulLabel(optName, code)) return optName;

  const rows = Array.isArray(tableData) ? tableData : [];
  for (const row of rows) {
    if (String(row.materialCode).trim() !== code) continue;
    const desc = materialDescFromRow(row);
    if (isMeaningfulLabel(desc, code)) return desc;
  }

  const fromMap = buildMaterialCodeDescMap(tableData, filterOptions).get(code);
  if (fromMap && isMeaningfulLabel(fromMap, code)) return fromMap;

  return "—";
}

/** Resolve site cluster via options, table rows, then cascading `/get_filters`. */
export async function resolveSiteClusterForCodeAsync(
  siteCode,
  selectedFilters,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = String(siteCode ?? "").trim();
  if (!code) return "—";

  const fromOpt = lookupCorrectionOptionLabel(code, correctionOptions?.siteCode);
  if (fromOpt) return fromOpt;

  const local = resolveSiteClusterForCode(
    code,
    tableData,
    filterOptions,
    correctionOptions,
  );
  if (local !== "—") return local;

  try {
    const data = await tntLinkageGetFilters({
      ...(selectedFilters || {}),
      unit_code: [code],
    });
    const clusters = (data.site_cluster || [])
      .map((c) => String(c).trim())
      .filter(Boolean);
    const paired = clusters.find((cl) => isMeaningfulLabel(cl, code));
    if (paired) return paired;
    if (clusters.length > 0) return clusters[0];
  } catch (error) {
    console.error("Failed to resolve site cluster for code:", code, error);
  }
  return "—";
}

/** Resolve material description via options, table rows, then cascading `/get_filters`. */
export async function resolveMaterialDescForCodeAsync(
  materialCode,
  selectedFilters,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = String(materialCode ?? "").trim();
  if (!code) return "—";

  const fromOpt = lookupCorrectionOptionLabel(code, correctionOptions?.material);
  if (fromOpt) return fromOpt;

  const local = resolveMaterialDescForCode(
    code,
    tableData,
    filterOptions,
    correctionOptions,
  );
  if (local !== "—") return local;

  try {
    const data = await tntLinkageGetFilters({
      ...(selectedFilters || {}),
      item_code: [code],
    });
    const descs = (data.item_description || [])
      .map((d) => String(d).trim())
      .filter(Boolean);
    const paired = descs.find((d) => isMeaningfulLabel(d, code));
    if (paired) return paired;
    if (descs.length > 0) return descs[0];
  } catch (error) {
    console.error("Failed to resolve material description for code:", code, error);
  }
  return "—";
}

export function sortTntCorrectionEntries(entries) {
  return [...entries].sort((a, b) =>
    String(a.code ?? "").localeCompare(String(b.code ?? "")),
  );
}

/** Dropdown options `{ code, name }` for reject-match correction row. */
export function buildCorrectionOptionsFromFilters(filterOptions, tableData) {
  const rows = Array.isArray(tableData) ? tableData : [];
  const opts = filterOptions && typeof filterOptions === "object" ? filterOptions : {};

  const siteMap = buildSiteCodeClusterMap(tableData, filterOptions);
  filterOptionList(opts, "unit_code", "siteCode").forEach((code) => {
    const k = String(code).trim();
    if (!k || siteMap.has(k)) return;
    const cluster = resolveSiteClusterForCode(k, rows, filterOptions, null);
    siteMap.set(k, cluster);
  });

  const vendorMap = new Map();
  rows.forEach((row) => {
    const code = row.vendorCode != null ? String(row.vendorCode).trim() : "";
    if (!code) return;
    const name = String(row.agentVendorName || code).trim();
    vendorMap.set(code, name);
  });
  filterOptionList(opts, "vendor_name", "vendor").forEach((name) => {
    const n = String(name).trim();
    if (!n) return;
    const match = rows.find((r) => String(r.agentVendorName || "").trim() === n);
    if (match?.vendorCode) {
      const code = String(match.vendorCode).trim();
      if (code) vendorMap.set(code, String(match.agentVendorName || code).trim());
    }
  });

  const materialMap = buildMaterialCodeDescMap(tableData, filterOptions);
  filterOptionList(opts, "item_code", "materialId").forEach((code) => {
    const k = String(code).trim();
    if (!k || materialMap.has(k)) return;
    const desc = resolveMaterialDescForCode(k, rows, filterOptions, null);
    materialMap.set(k, desc);
  });

  return {
    siteCode: sortTntCorrectionEntries(
      [...siteMap.entries()].map(([code, name]) => ({ code, name })),
    ),
    vendor: sortTntCorrectionEntries(
      [...vendorMap.entries()].map(([code, name]) => ({ code, name })),
    ),
    material: sortTntCorrectionEntries(
      [...materialMap.entries()].map(([code, name]) => ({ code, name })),
    ),
  };
}

function getTrimmedSelectionValue(value) {
  return String(value ?? "").trim();
}

function resolveVendorName(
  vendorCode,
  selections,
  vendorOptions,
) {
  const presetName = getTrimmedSelectionValue(
    selections.agentVendorName,
  );

  if (presetName) {
    return presetName;
  }

  const vendor = vendorOptions.find(
    (option) =>
      getTrimmedSelectionValue(option?.code) ===
      vendorCode,
  );

  return vendor?.name;
}

function applySiteOverride(
  override,
  selections,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = getTrimmedSelectionValue(
    selections?.siteCode,
  );

  if (!code) {
    return;
  }

  override.siteCode = code;

  const presetCluster = getTrimmedSelectionValue(
    selections.siteCluster,
  );

  override.siteCluster =
    presetCluster ||
    resolveSiteClusterForCode(
      code,
      tableData,
      filterOptions,
      correctionOptions,
    );
}

function applyVendorOverride(
  override,
  selections,
  vendorOptions,
) {
  const vendorCode = getTrimmedSelectionValue(
    selections?.vendorCode,
  );

  if (!vendorCode) {
    return;
  }

  override.vendorCode = vendorCode;

  const vendorName = resolveVendorName(
    vendorCode,
    selections,
    vendorOptions,
  );

  if (vendorName) {
    override.agentVendorName = vendorName;
  }
}

function applyMaterialOverride(
  override,
  selections,
  tableData,
  filterOptions,
  correctionOptions,
) {
  const code = getTrimmedSelectionValue(
    selections?.materialCode,
  );

  if (!code) {
    return;
  }

  override.materialCode = code;

  const presetDesc = getTrimmedSelectionValue(
    selections.agentMaterialDesc,
  );

  override.agentMaterialDesc =
    presetDesc ||
    resolveMaterialDescForCode(
      code,
      tableData,
      filterOptions,
      correctionOptions,
    );
}
/**
 * Resolve in-flight dropdown picks into Agent Data column overrides.
 */
export function buildRowOverrideFromSelections(
  selections,
  correctionOptions,
  tableData,
  filterOptions,
) {
  const override = {};

  const opts =
    correctionOptions || {
      siteCode: [],
      vendor: [],
      material: [],
    };

  applySiteOverride(
    override,
    selections,
    tableData,
    filterOptions,
    correctionOptions,
  );

  applyVendorOverride(
    override,
    selections,
    opts.vendor,
  );

  applyMaterialOverride(
    override,
    selections,
    tableData,
    filterOptions,
    correctionOptions,
  );

  return override;
}

export function buildTntUserPrefsPayload(email, filters) {
  return {
    user_email: email,
    screen_id: TNT_USER_PREFS_SCREEN_ID,
    columns: [],
    filters: filters.map((f) => ({
      name:
        f.prefName ||
        f.filterKey ||
        String(f.label || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_"),
      priority: f.DisplayOrder,
      is_active: f.isFavourite,
    })),
  };
}
